import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { observatorioApi, type ObservatorioNicho } from '../api/observatorio'
import { ApiError } from '../lib/api'

// Nicho do cliente no Observatório da Lucro Digital e os locais onde ele atua (estados e
// cidades com dado próprio no Planejador do Google). O Brasil entra sempre como referência.
// Sem o nicho no catálogo, dá para pedir um novo (entra proposto para a revisão de segunda).

const inputCls =
  'w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none disabled:bg-slate-50 disabled:text-slate-500'

const MAX_RESULTADOS = 8

const semAcento = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()

// Nichos cujo nome ou sinônimo contém o texto (sem acento nem caixa). Nome que começa com o
// texto vem antes; depois nome que contém; por último quem só bate por sinônimo.
function filtrarNichos(nichos: ObservatorioNicho[], texto: string) {
  const t = semAcento(texto)
  if (!t) return []
  const achados: { n: ObservatorioNicho; ordem: number; via: string | null }[] = []
  for (const n of nichos) {
    const nome = semAcento(n.nome)
    if (nome.startsWith(t)) achados.push({ n, ordem: 0, via: null })
    else if (nome.includes(t)) achados.push({ n, ordem: 1, via: null })
    else {
      const via = n.sinonimos.find((s) => semAcento(s).includes(t))
      if (via) achados.push({ n, ordem: 2, via })
    }
  }
  return achados
    .sort(
      (a, b) =>
        a.ordem - b.ordem || Number(b.n.noMapa) - Number(a.n.noMapa) || a.n.nome.localeCompare(b.n.nome, 'pt-BR')
    )
    .slice(0, MAX_RESULTADOS)
}

function Etiqueta({ n }: { n: ObservatorioNicho }) {
  if (n.noMapa)
    return <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[11px] font-medium text-emerald-700">No Mapa</span>
  return (
    <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-700">
      {n.status === 'ATIVO' ? 'aguardando dados' : 'proposto'}
    </span>
  )
}

export function ObservatorioPicker({
  nicho,
  geos,
  onChange,
  disabled,
  clientName,
}: {
  nicho: string
  geos: number[]
  onChange: (nicho: string, geos: number[]) => void
  disabled?: boolean
  clientName: string
}) {
  const qc = useQueryClient()
  const { data, error, isLoading } = useQuery({
    queryKey: ['observatorio-catalogo'],
    queryFn: () => observatorioApi.catalogo(),
    retry: false,
    staleTime: 10 * 60_000,
  })
  const [cidade, setCidade] = useState('')
  const [busca, setBusca] = useState('')
  const [listaAberta, setListaAberta] = useState(false)
  const [destaque, setDestaque] = useState(0)
  const [trocando, setTrocando] = useState(false)
  const [pedindo, setPedindo] = useState(false)
  const [novoNome, setNovoNome] = useState('')
  const [novoPublico, setNovoPublico] = useState<'SERVICOS' | 'ECOMMERCE'>('SERVICOS')
  const [novoSinonimos, setNovoSinonimos] = useState('')
  const [aviso, setAviso] = useState<string | null>(null)

  const pedir = useMutation({
    mutationFn: () =>
      observatorioApi.pedirNicho({
        nome: novoNome.trim(),
        publico: novoPublico,
        sinonimos: novoSinonimos
          .split(/[\n,;]+/)
          .map((s) => s.trim())
          .filter(Boolean),
        clientName,
      }),
    onSuccess: (r) => {
      onChange(r.nicho.slug, geos)
      qc.invalidateQueries({ queryKey: ['observatorio-catalogo'] })
      setBusca('')
      setTrocando(false)
      setPedindo(false)
      setNovoNome('')
      setNovoSinonimos('')
      setAviso(
        r.criado
          ? `Pedido enviado: "${r.nicho.nome}" entrou como proposto no Observatório. Os dados chegam depois da ` +
              'coleta de domingo e da aprovação na revisão de segunda. Salve o cliente para guardar o nicho.'
          : `Já existia: "${r.nicho.nome}" (${r.nicho.status.toLowerCase()}). Selecionado — salve o cliente.`
      )
    },
    onError: (err) => setAviso(err instanceof ApiError ? err.message : 'Erro ao pedir o nicho'),
  })

  if (isLoading) return <p className="text-sm text-slate-500">Carregando o catálogo do Observatório…</p>

  // Sem catálogo (token não configurado ou NovaLD fora): dá para digitar o slug — a
  // demanda e o CPC atual vêm do pacote público mesmo sem token.
  if (error || !data) {
    return (
      <div className="space-y-2">
        <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Catálogo indisponível: {error instanceof ApiError ? error.message : 'erro ao carregar'}. Dá para
          informar o slug do nicho à mão (ex.: <code className="font-mono">consorcio</code>).
        </p>
        <input
          value={nicho}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value.trim().toLowerCase(), geos)}
          placeholder="slug do nicho"
          className={inputCls}
        />
      </div>
    )
  }

  const atual = data.nichos.find((n) => n.slug === nicho)
  const resultados = filtrarNichos(data.nichos, busca)
  const escolher = (slug: string) => {
    onChange(slug, geos)
    setBusca('')
    setListaAberta(false)
    setTrocando(false)
  }
  const pedirComOTexto = () => {
    setNovoNome(busca.trim())
    setListaAberta(false)
    setPedindo(true)
  }
  const teclas = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      setListaAberta(true)
      setDestaque((d) =>
        e.key === 'ArrowDown' ? Math.min(d + 1, Math.max(resultados.length - 1, 0)) : Math.max(d - 1, 0)
      )
    } else if (e.key === 'Enter') {
      // Enter aqui não pode enviar o formulário do cliente (este fica dentro dele).
      e.preventDefault()
      const r = resultados[destaque]
      if (r) escolher(r.n.slug)
    } else if (e.key === 'Escape') {
      if (busca) setBusca('')
      else setTrocando(false)
    }
  }
  const estados = data.geos.filter((g) => g.tipo === 'ESTADO')
  const cidades = data.geos.filter((g) => g.tipo === 'CIDADE')
  const nomeDoGeo = new Map(data.geos.map((g) => [g.id, g.tipo === 'CIDADE' ? `${g.nome} (${g.uf})` : g.nome]))
  const toggle = (id: number) =>
    onChange(nicho, geos.includes(id) ? geos.filter((g) => g !== id) : [...geos, id])
  const cidadesEscolhidas = geos.filter((g) => cidades.some((c) => c.id === g))

  return (
    <div className="space-y-4">
      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700">Nicho</label>
        {nicho && !trocando ? (
          <div className="flex items-center justify-between gap-3 rounded-md border border-slate-300 px-3 py-2 text-sm">
            <span className="flex flex-wrap items-center gap-2">
              <span className="font-medium text-slate-800">{atual?.nome ?? nicho}</span>
              {atual && <Etiqueta n={atual} />}
            </span>
            {!disabled && (
              <span className="flex shrink-0 gap-3 text-xs font-medium">
                <button type="button" onClick={() => setTrocando(true)} className="text-brand-700 hover:underline">
                  Trocar
                </button>
                <button
                  type="button"
                  onClick={() => onChange('', geos)}
                  className="text-slate-500 hover:text-red-600"
                >
                  Remover
                </button>
              </span>
            )}
          </div>
        ) : (
          <div className="relative">
            <input
              value={busca}
              disabled={disabled}
              autoFocus={trocando}
              onChange={(e) => {
                setBusca(e.target.value)
                setDestaque(0)
                setListaAberta(true)
              }}
              onFocus={() => setListaAberta(true)}
              onBlur={() => setListaAberta(false)}
              onKeyDown={teclas}
              placeholder={`Digite para buscar entre ${data.nichos.length} nichos (ex.: consórcio)`}
              role="combobox"
              aria-expanded={listaAberta && !!busca.trim()}
              aria-autocomplete="list"
              className={inputCls}
            />
            {listaAberta && busca.trim() && (
              <ul
                role="listbox"
                className="absolute z-10 mt-1 max-h-72 w-full overflow-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg"
              >
                {resultados.map((r, i) => (
                  <li key={r.n.slug} role="option" aria-selected={i === destaque}>
                    <button
                      type="button"
                      // mousedown tiraria o foco do campo e fecharia a lista antes do clique.
                      onMouseDown={(e) => e.preventDefault()}
                      onMouseEnter={() => setDestaque(i)}
                      onClick={() => escolher(r.n.slug)}
                      className={`flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left text-sm ${
                        i === destaque ? 'bg-brand-50' : ''
                      }`}
                    >
                      <span className="min-w-0">
                        <span className="text-slate-800">{r.n.nome}</span>
                        {r.via && <span className="ml-1.5 text-xs text-slate-400">({r.via})</span>}
                      </span>
                      <Etiqueta n={r.n} />
                    </button>
                  </li>
                ))}
                {resultados.length === 0 && (
                  <li className="px-3 py-2 text-xs text-slate-500">
                    Nenhum nicho com “{busca.trim()}”.{' '}
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={pedirComOTexto}
                      className="font-medium text-brand-700 hover:underline"
                    >
                      Pedir como nicho novo
                    </button>
                  </li>
                )}
              </ul>
            )}
            {trocando ? (
              <button
                type="button"
                onClick={() => {
                  setBusca('')
                  setTrocando(false)
                }}
                className="mt-1 text-xs text-slate-500 hover:underline"
              >
                Cancelar (manter {atual?.nome ?? nicho})
              </button>
            ) : (
              <p className="mt-1 text-xs text-slate-500">Sem nicho, a análise não usa o Observatório.</p>
            )}
          </div>
        )}
        {atual && !atual.noMapa && (
          <p className="mt-1 text-xs text-amber-700">
            Ainda sem pacote no Mapa: a análise usa só o que já existir (buscas, lance) até a aprovação.
          </p>
        )}
        {nicho && !atual && (
          <p className="mt-1 text-xs text-amber-700">Este nicho não está no catálogo (recusado ou removido).</p>
        )}
        {!disabled && !pedindo && (
          <button
            type="button"
            onClick={() => setPedindo(true)}
            className="mt-2 text-xs font-medium text-brand-700 hover:underline"
          >
            Não achou o nicho do cliente? Pedir nicho novo
          </button>
        )}
      </div>

      {pedindo && (
        <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
          <div className="grid gap-3 sm:grid-cols-[1fr_160px]">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-700">Nome do nicho</label>
              <input
                value={novoNome}
                onChange={(e) => setNovoNome(e.target.value)}
                // Enter aqui não pode enviar o formulário do cliente (este fica dentro dele).
                onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                maxLength={60}
                placeholder="ex.: Compressores de ar"
                className={inputCls}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-700">Público</label>
              <select
                value={novoPublico}
                onChange={(e) => setNovoPublico(e.target.value as 'SERVICOS' | 'ECOMMERCE')}
                className={inputCls}
              >
                <option value="SERVICOS">Serviços</option>
                <option value="ECOMMERCE">E-commerce</option>
              </select>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-700">
              Como os clientes procuram (até 10, sem cidade)
            </label>
            <textarea
              value={novoSinonimos}
              onChange={(e) => setNovoSinonimos(e.target.value)}
              rows={2}
              placeholder={'compressor de ar, locação de compressor, manutenção de compressor'}
              className={inputCls}
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={pedir.isPending || novoNome.trim().length < 2}
              onClick={() => {
                setAviso(null)
                pedir.mutate()
              }}
              className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {pedir.isPending ? 'Enviando…' : 'Pedir ao Observatório'}
            </button>
            <button
              type="button"
              onClick={() => setPedindo(false)}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-white"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
      {aviso && <p className="rounded-md bg-slate-100 px-3 py-2 text-xs text-slate-700">{aviso}</p>}

      {nicho && (
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Estados onde o cliente atua</label>
            <div className="flex flex-wrap gap-1.5">
              {estados.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  disabled={disabled}
                  onClick={() => toggle(g.id)}
                  title={g.nome}
                  className={`rounded border px-2 py-1 font-mono text-xs ${
                    geos.includes(g.id)
                      ? 'border-brand-600 bg-brand-50 text-brand-700'
                      : 'border-slate-300 text-slate-500 hover:border-slate-400'
                  } disabled:opacity-60`}
                >
                  {g.uf ?? g.nome}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Cidades (só as que têm dado próprio no Planejador)
            </label>
            <div className="flex gap-2">
              <select value={cidade} disabled={disabled} onChange={(e) => setCidade(e.target.value)} className={inputCls}>
                <option value="">Escolha uma cidade…</option>
                {cidades
                  .filter((c) => !geos.includes(c.id))
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nome} ({c.uf})
                    </option>
                  ))}
              </select>
              <button
                type="button"
                disabled={disabled || !cidade}
                onClick={() => {
                  toggle(Number(cidade))
                  setCidade('')
                }}
                className="rounded-md border border-slate-300 px-3 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              >
                Adicionar
              </button>
            </div>
            {cidadesEscolhidas.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {cidadesEscolhidas.map((g) => (
                  <span key={g} className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-xs text-brand-700">
                    {nomeDoGeo.get(g) ?? g}
                    {!disabled && (
                      <button type="button" onClick={() => toggle(g)} className="text-brand-500 hover:text-brand-800" aria-label="Remover">
                        ×
                      </button>
                    )}
                  </span>
                ))}
              </div>
            )}
          </div>
          <p className="text-xs text-slate-500">
            O Brasil entra sempre como referência. Sem estado nem cidade, a análise usa só o Brasil.
            {data.versao && ` Base do Observatório: semana de ${data.versao.semana.split('-').reverse().join('/')}.`}
          </p>
        </div>
      )}
    </div>
  )
}
